import { Category } from "../types/form";
import type { FormReadinessMissingItem } from "../API/applicationAPI";

/**
 * A pruned slice of the form's categoryTree containing only the
 * categories that have at least one missing question (directly or
 * through a descendant). Used by the SubmitConfirmationModal's
 * "missing" state to show the user which sections still need
 * attention without overwhelming them with the full question list.
 *
 * Each entry retains the original Category fields plus a pruned
 * `subcategories` array (only the branches with missing content).
 */
export interface MissingCategoryNode {
  id: number;
  name: string;
  /** First missing-question id in this category, used for jump-to-question on click. */
  firstMissingQuestionId: number | null;
  subcategories: MissingCategoryNode[];
}

/**
 * Build the pruned tree.
 *
 * @param missing       Flat list of unanswered visible questions from
 *                      the readiness response.
 * @param categoryTree  The form's full category tree (from formStructure).
 */
export function buildMissingCategoriesTree(
  missing: FormReadinessMissingItem[],
  categoryTree: Category[] | undefined,
): MissingCategoryNode[] {
  if (!categoryTree?.length || missing.length === 0) return [];

  // Index: for each category id, the first missing question id we
  // encountered for it. Lets a category click scroll to a meaningful
  // anchor instead of just opening the section blank.
  const firstMissingByCategoryId = new Map<number, number>();
  for (const m of missing) {
    if (m.categoryId == null) continue;
    if (!firstMissingByCategoryId.has(m.categoryId)) {
      firstMissingByCategoryId.set(m.categoryId, m.questionId);
    }
  }
  const missingCategoryIds = new Set(firstMissingByCategoryId.keys());

  /**
   * Walk a category subtree, returning a pruned MissingCategoryNode if
   * the category itself or any descendant is in `missingCategoryIds`.
   * Returns null if nothing in the subtree has missing content (so the
   * caller can drop the branch entirely).
   */
  const prune = (c: Category): MissingCategoryNode | null => {
    const pruned: MissingCategoryNode[] = [];
    for (const sub of c.subcategories ?? []) {
      const p = prune(sub);
      if (p) pruned.push(p);
    }
    const selfHasMissing = missingCategoryIds.has(c.id);
    if (!selfHasMissing && pruned.length === 0) return null;
    return {
      id: c.id,
      name: c.name,
      firstMissingQuestionId: firstMissingByCategoryId.get(c.id) ?? null,
      subcategories: pruned,
    };
  };

  const out: MissingCategoryNode[] = [];
  for (const top of categoryTree) {
    const p = prune(top);
    if (p) out.push(p);
  }
  return out;
}
